import type {BlockContent, PhrasingContent, Root as MarkdownTree, RootContent as MarkdownNode} from 'mdast'
import {Node as ProseMirrorTree} from 'prosemirror-model'
import remarkGfm from 'remark-gfm'
import remarkParse from 'remark-parse'
import remarkStringify from 'remark-stringify'
import {unified} from 'unified'

import {schema} from './hybrid-markdown-schema'

const unifiedRemark = unified().use(remarkParse).use(remarkGfm).use(remarkStringify)

/** Parse a markdown string to an mdast tree. */
export function parseToMdast(markdown: string): MarkdownTree {
  // TODO: It would be extremely useful to extend the node data to add the raw source / delimeters, so we can use
  // that to create decorations and other metadata. The current approach of getting the source value and
  // comparing to the real value to determine delimeters is pretty hacky.
  return unifiedRemark.parse(markdown)
}

/** Type check if a node is positioned. */
export function isPositionedNode(node: MarkdownNode): node is MarkdownNode & {
  position: {start: {offset: number}; end: {offset: number}}
} {
  return node.position !== undefined && node.position.start !== undefined && node.position.end !== undefined
}

/** Get the source text for a node, based on its position in the source document. */
export function getNodeSource(node: MarkdownNode, source: string): string | null {
  if (!isPositionedNode(node)) return null
  return source.slice(node.position.start.offset, node.position.end.offset)
}

function isNotNull<T>(value: T | null): value is T {
  return value !== null
}

/** Get the source text for several nodes and join them together. */
function getNodeSources(nodes: MarkdownNode[], source: string): ProseMirrorTree {
  return schema.text(nodes.map(child => getNodeSource(child, source) || '').join(''))
}

function convertMdastNodeToProseMirror(node: MarkdownNode, source: string): ProseMirrorTree | null {
  switch (node.type) {
    // paragraphs and headings get the plain Markdown source of their content as the text, since all rendering is done
    // by decorations
    case 'paragraph':
      return schema.nodes.paragraph.createChecked(null, getNodeSources(node.children, source))
    case 'heading':
      // TODO: heading rendering should _probably_ just be done with decorations as well, making Paragraph the only
      // hybrid node
      return schema.nodes.heading.createChecked({level: node.depth}, [
        schema.text(`${'#'.repeat(node.depth)} `),
        getNodeSources(node.children, source),
      ])
    case 'thematicBreak':
      return schema.nodes.thematicBreak.createChecked()
    case 'blockquote':
      // blockquotes are (for now) the only flow content that contains flow content, so they are the only content that
      // we recurse for
      return schema.nodes.blockquote.createChecked(
        null,
        node.children.map(child => convertMdastNodeToProseMirror(child, source)).filter(isNotNull),
      )
    default: {
      const nodeSource = getNodeSource(node, source)
      return nodeSource ? schema.nodes.unsupported.createChecked(null, schema.text(nodeSource)) : null
    }
  }
}

/** Parse a Markdown string to the ProseMirror document schema. */
export function parseMarkdownToProseMirror(source: string): ProseMirrorTree {
  const mdTree = parseToMdast(source)
  return schema.nodes.doc.createChecked(
    null,
    mdTree.children.map(node => convertMdastNodeToProseMirror(node, source)).filter(isNotNull),
  )
}

function convertProseMirrorNodeToMdast(node: ProseMirrorTree): MarkdownNode {
  switch (node.type.name) {
    case 'paragraph':
    case 'unsupported':
      return {
        type: 'paragraph',
        children: parseToMdast(node.textContent).children as PhrasingContent[],
      }
    case 'heading': {
      // TODO: Find a way to make this typesafe
      const attrs = node.attrs as {level: 1 | 2 | 3 | 4 | 5 | 6}
      return {
        type: 'heading',
        depth: attrs.level,
        children: parseToMdast(
          // hack: remove the leading delimiter since it will get re-added by the serializer
          node.textContent.slice(attrs.level + 1),
        ).children as PhrasingContent[],
      }
    }
    case 'blockquote':
      return {
        type: 'blockquote',
        children: node.children.map(convertProseMirrorNodeToMdast).filter(isNotNull) as BlockContent[],
      }
    case 'rule':
      return {type: 'thematicBreak'}
    default:
      return {type: 'text', value: node.textContent}
  }
}

export function convertProseMirrorDocToMdast(doc: ProseMirrorTree): MarkdownTree {
  return {
    type: 'root',
    children: doc.children.map(convertProseMirrorNodeToMdast).filter(isNotNull) as MarkdownNode[],
  }
}

/** Convert a ProseMirror document to a Markdown string. */
export function serializeProseMirrorDocToMarkdown(doc: ProseMirrorTree): string {
  return unifiedRemark.stringify(convertProseMirrorDocToMdast(doc))
}
